/**
 * Centralized Logging Utility
 *
 * Provides environment-aware logging that automatically disables
 * console output in production builds while maintaining development
 * debugging capabilities.
 */

import { environment } from "../config/environment.js";

/**
 * Logger class that respects environment configuration
 */
class Logger {
  constructor() {
    this.isEnabled = environment.logging.enableConsoleLogging;
    this.logLevel = environment.logging.logLevel;
  }

  /**
   * Log info message (only in development)
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   */
  info(message, data = null) {
    if (!this.isEnabled) return;

    if (data) {
      console.log(`ℹ️ ${message}`, data);
    } else {
      console.log(`ℹ️ ${message}`);
    }
  }

  /**
   * Log success message (only in development)
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   */
  success(message, data = null) {
    if (!this.isEnabled) return;

    if (data) {
      console.log(`✅ ${message}`, data);
    } else {
      console.log(`✅ ${message}`);
    }
  }

  /**
   * Log warning message (only in development)
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   */
  warn(message, data = null) {
    if (!this.isEnabled) return;

    if (data) {
      console.warn(`⚠️ ${message}`, data);
    } else {
      console.warn(`⚠️ ${message}`);
    }
  }

  /**
   * Log error message (always shown, even in production)
   * @param {string} message - Error message
   * @param {Error|Object} error - Error object or additional data
   */
  error(message, error = null) {
    // Errors are always logged, even in production
    if (error) {
      console.error(`❌ ${message}`, error);
    } else {
      console.error(`❌ ${message}`);
    }
  }

  /**
   * Log debug message (only in development)
   * @param {string} message - Debug message
   * @param {Object} data - Additional data to log
   */
  debug(message, data = null) {
    if (!this.isEnabled || this.logLevel !== "debug") return;

    if (data) {
      console.debug(`🔍 ${message}`, data);
    } else {
      console.debug(`🔍 ${message}`);
    }
  }

  /**
   * Log API request (only in development)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {Object} data - Request data
   */
  apiRequest(method, url, data = null) {
    if (!this.isEnabled) return;

    if (data) {
      console.log(`🔄 API ${method.toUpperCase()}: ${url}`, data);
    } else {
      console.log(`🔄 API ${method.toUpperCase()}: ${url}`);
    }
  }

  /**
   * Log API response (only in development)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {Object} response - Response data
   */
  apiResponse(method, url, response = null) {
    if (!this.isEnabled) return;

    if (response) {
      console.log(`📊 API ${method.toUpperCase()} Response: ${url}`, response);
    } else {
      console.log(`📊 API ${method.toUpperCase()} Response: ${url}`);
    }
  }

  /**
   * Log component lifecycle (only in development)
   * @param {string} component - Component name
   * @param {string} action - Lifecycle action
   * @param {Object} data - Additional data
   */
  component(component, action, data = null) {
    if (!this.isEnabled) return;

    if (data) {
      console.log(`🎯 ${component}: ${action}`, data);
    } else {
      console.log(`🎯 ${component}: ${action}`);
    }
  }

  /**
   * Group related logs (only in development)
   * @param {string} label - Group label
   * @param {Function} callback - Function containing logs to group
   */
  group(label, callback) {
    if (!this.isEnabled) {
      // Still execute callback but without grouping
      callback();
      return;
    }

    console.group(`📁 ${label}`);
    callback();
    console.groupEnd();
  }
}

// Export singleton instance
export default new Logger();
